import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { DataService } from 'app/shared/services/dataService';
import { Recommendation, RecommendationTrackerService } from 'app/shared/services/recommendationTrackerService';
import { UserService } from 'app/shared/services/userService';
import { EventAggregator, EventList } from 'app/shared/utility/EventAggregator';
import { Common } from 'app/shared/utility/common';
import { EventTypes } from 'app/shared/utility/constants';

@Component({
  selector: 'recommendation-tracker',
  templateUrl: './recommendation-tracker.component.html',
  styleUrls: ['./recommendation-tracker.component.css']
})
export class RecommendationTrackerComponent implements OnInit, OnDestroy {
  isAdmin: boolean;
  loading: boolean;
  yearOptions: Array<any> = [];
  quarterOptions: Array<any> = [];
  report?: any;
  showScrollToTop: boolean;
  selectedJurisdiction: string;
  jurisdictionOptions: Array<any> = [];
  selectedYear: number = new Date().getFullYear();
  selectedQuarter: number = 1;
  recommendationSections: any = {
    "Category": [
      "Care Integration",
      "Harm Reduction",
      "Law Enforcement and Forensic Intervention",
      "Courts and Corrections System Intervention",
      "Intervention with Underserved or Special Populations",
      "Service Enhancement",
      "Information Sharing",
      "Standardization",
      "Other"
    ],
    "Target Group": [
      "People who use drugs",
      "Healthcare providers",
      "Policymakers/Legislators",
      "General Public",
      "Family Members",
      "Other"
    ],
    "Agency": [
      "Law enforcement",
      "Hospital",
      "Healthcare Provider",
      "Pharmacy",
      "Judicial System",
      "Health Department Treatment",
      "Health Department Prevention",
      "EMS",
      "Detention Center",
      "Education",
      "Private Behavioral Health",
      "Social Services",
      "Other Local Government Agency",
      "Other"
    ]
  };

  recommendations: any = {};
  subsectionLoading: any = {};
  showSubsection: any = {};
  showFirstFive: any = {};
  private navState: any;

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private dataService: DataService,
    private userService: UserService,
    private recommendationTrackerService: RecommendationTrackerService
  ) {
    this.navState = this.router.getCurrentNavigation()?.extras.state;
  }

  ngOnDestroy(): void {
  }

  ngOnInit(): void {
    const years = Common.getYearsDropDown(2022);
    this.yearOptions = [];
    this.quarterOptions = [];
    for (let i = 0; i < years.length; i++) {
      this.yearOptions.push({ name: years[i].toString(), value: years[i].toString() });
    }
    for (let i = 0; (i * 3) + 1 <= (new Date().getMonth() + 1); i++) {
      this.quarterOptions.push({ name: "Q" + (i + 1), value: "" + (i + 1) });
      this.selectedQuarter = (i + 1);
    }

    this.userService.GetUserGroups().then(groups => {
      this.jurisdictionOptions = this.jurisdictionOptions.concat(groups.map((g) => ({ name: g, value: g })));

      if (this.jurisdictionOptions.length === 1) {
        this.selectedJurisdiction = this.jurisdictionOptions[0].value;
      }

      this.getRecommendationTrackerReport();
    });

    this.dataService.getAccess().subscribe((access) => {
      this.isAdmin = access.IsAdmin;
    });

    this.loading = false;
  }

  populateRecommendations() {
    var promises = [];
    Object.keys(this.recommendationSections).forEach(section => {
      this.recommendations[section] = {};
      this.subsectionLoading[section] = {};
      this.showSubsection[section] = {};
      this.showFirstFive[section] = {};
      this.recommendationSections[section].forEach(subsection => {
        promises.push(this.getRecommendationsSectionSubsection(section, subsection).then(res => {
          this.recommendations[section][subsection] = res;
          this.subsectionLoading[section][subsection] = false;
          this.showSubsection[section][subsection] = false;
          this.showFirstFive[section][subsection] = true;
        }));
      });
    });
    return Promise.all(promises);
  }

  formatDate(dateString) {
    const date = new Date(dateString);
    const options = {
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      month: "numeric",
      year: "numeric",
    } as const;

    return date.toLocaleString("en", options);
  }

  formatSelectedCountyName() {
    return this.selectedJurisdiction.replace('County', '') + (this.selectedJurisdiction.indexOf("City") >= 0 ? "" : " County");
  }

  getRecommendationsSectionSubsection(section, subsection) {
    return this.recommendationTrackerService.getRecommendationsSectionSubsection(
      section, subsection, this.selectedQuarter, this.selectedYear, this.selectedJurisdiction
    );
  }

  setScrollToTopVisibility(canShow) {
    this.showScrollToTop = canShow;
  }

  keepOriginalOrder = (a, b) => a.key;

  collapseSubsection(section, subsection, forceCollapse = undefined) {
    if (forceCollapse != undefined) {
      this.showSubsection[section][subsection] = forceCollapse;
      this.showFirstFive[section][subsection] = false;
    } else {
      this.showSubsection[section][subsection] = !this.showSubsection[section][subsection];
      this.showFirstFive[section][subsection] = false;
    }
  }

  expandAll(expand: boolean) {
    Object.keys(this.recommendationSections).forEach(section => {
      this.expandSection(section, expand);
    });
  }

  expandSection(section: string, expand: boolean) {
    this.recommendationSections[section].forEach(subsection => {
      this.collapseSubsection(section, subsection, expand);
    });
  }

  recommendationsSubmit(section, subsection) {
    let inputField = <HTMLInputElement>document.getElementById('field-' + section + '-' + subsection);
    let btIcon = document.getElementById('icon-' + section + '-' + subsection);
    if (!inputField.classList.contains("fieldvisible")) {
      inputField.classList.add("fieldvisible");
      btIcon.innerHTML = "send";
      return;
    }

    inputField.classList.remove("fieldvisible");
    btIcon.innerHTML = "add";
    if (inputField.value.trim() === "") {
      return;
    }
    var newRecommendation = new Recommendation();
    newRecommendation.Content = inputField.value;
    newRecommendation.Jurisdiction = this.selectedJurisdiction;
    newRecommendation.Quarter = this.selectedQuarter;
    newRecommendation.Year = this.selectedYear;
    newRecommendation.Section = section;
    newRecommendation.SubSection = subsection;

    this.subsectionLoading[section][subsection] = true;
    inputField.value = "";
    this.recommendationTrackerService.AddRecommendation(newRecommendation)
      .then(res => {
        this.getRecommendationsSectionSubsection(section, subsection).then(res => {
          this.recommendations[section][subsection] = res;
          this.subsectionLoading[section][subsection] = false;
        });
      });
  }

  onYearChange() {
    var lastMonth = new Date().getFullYear() == this.selectedYear ? (new Date().getMonth() + 1) : 12;
    this.quarterOptions = [];
    for (let i = 0; (i * 3) + 1 <= lastMonth; i++) {
      this.quarterOptions.push({ name: "Q" + (i + 1), value: "" + (i + 1) });
      this.selectedQuarter = (i + 1);
    }
    this.getRecommendationTrackerReport();
  }

  getRecommendationTrackerReport() {
    var scrolledSectionId = null;
    if (this.navState) {
      this.selectedQuarter = this.navState.quarter;
      this.selectedYear = this.navState.year;
      this.selectedJurisdiction = this.navState.jurisdiction;
      scrolledSectionId = this.navState.scrolledSectionId;
      this.navState = null;
    }
    if (this.selectedJurisdiction) {
      this.loading = true;
      this.recommendationTrackerService.getRecommendationTrackerReport(this.selectedQuarter, this.selectedYear, this.selectedJurisdiction)
        .then(res => {
          this.report = {};
          for (var section of Object.keys(this.recommendationSections)) {
            this.report[section] = {}
            for (var subsection of this.recommendationSections[section]) {
              this.report[section][subsection] = {
                QuarterTotal: 0,
                YearTotal: 0
              };
            }
          }

          for (var group of res.RecommendationTrackerDataGroups) {
            var section = group.Name;
            for (var data of group.RecommendationTrackerData) {
              var fields = data.Field.split(",");
              for (var field of fields) {
                field = field.trim();
                Object.keys(this.report[section]).forEach(subsection => {
                  if (subsection.toLowerCase() === field.toLowerCase()) {
                    field = subsection;
                  }
                });
                if (field in this.report[section]) {
                  this.report[section][field]['QuarterTotal'] += data.QuarterTotals;
                  this.report[section][field]['YearTotal'] += data.YearToDateTotals;
                }
              }
            }
          }

          this.populateRecommendations().then(res => {
            this.loading = false;
            if (scrolledSectionId) {
              setTimeout(() => {
                this.scrollTo(scrolledSectionId);
              }, 250);
            }
          });
        });
    }
  }

  scrollTo(section: string) {
    this.router.navigate([], { fragment: section }).then(res => {
      const element = document.getElementById(section);
      if (element != undefined) element.scrollIntoView();
    });
  }

  printReport() {
    var old_showSubsection = (JSON.parse(JSON.stringify(this.showSubsection)));
    var old_showFirstFive = (JSON.parse(JSON.stringify(this.showFirstFive)));
    this.expandAll(true);
    setTimeout(() => {
      window.print();
      this.showSubsection = old_showSubsection;
      this.showFirstFive = old_showFirstFive;
    }, 500);
  }

  generateCsv() {
    var content = "data:text/csv;charset=utf-8,";
    content += ",Quarter Date Selected - Total,Current Year to Date - Total\r\n";
    Object.keys(this.report).forEach(section => {
      content += section + ",,\r\n";
      Object.keys(this.report[section]).forEach(subsection => {
        content += subsection + "," + this.report[section][subsection].QuarterTotal + "," + this.report[section][subsection].YearTotal + "\r\n";
      });
    });
    return content;
  }

  formatDateTimeStamp() {
    const date = new Date();

    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const hour = date.getHours().toString().padStart(2, '0');
    const minute = date.getMinutes().toString().padStart(2, '0');

    return `${month}-${day}-${year}-${hour}-${minute}`;
  }

  downloadTable() {
    var csvName = this.formatSelectedCountyName().replace(" ", "") + "RecommendationTotals" + "-Q" + this.selectedQuarter + "-" + this.selectedYear + "-" + this.formatDateTimeStamp();
    var csvContent = this.generateCsv();
    console.log(csvContent);
    var encodedUri = encodeURI(csvContent);
    var link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", csvName + ".csv");
    document.body.appendChild(link);

    link.click();
  }

  showCaseList(section: any, subsection: any) {
    this.getRecommendationTrackerCases(section, subsection).then(caseList => {
      EventAggregator.publish(EventList.popupTriggered, {
        type: EventTypes.CaseList,
        value: {
          caseCategory: subsection,
          caseList: caseList,
          printMode: false,
          quarter: this.selectedQuarter,
          year: this.selectedYear,
          jurisdiction: this.selectedJurisdiction,
          scrolledSectionId: section + '-' + subsection
        }
      });
    });
  }

  getRecommendationTrackerCases(section: any, subsection: any) {
    return this.recommendationTrackerService.getRecommendationTrackerCases(this.selectedJurisdiction, this.selectedQuarter, this.selectedYear, subsection, section);
  }
}